// ============================================================================
// Authentication Types
// ============================================================================

/** #413 — the two alarm levels that have a label band, an icon and a muted look */
export type AlarmLevel = 'suspicious' | 'critical'

export interface User {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  phone_number: string | null
  phone_verified?: boolean
  profile_picture: string | null
  email_verified: boolean
  is_staff: boolean
  is_superuser: boolean
  language: 'en' | 'ja'
  theme: 'light' | 'dark' | 'system'
  /** #277 — order of the Cameras page for this user; absent from an older API = latest_event. */
  cameras_sort?: CamerasSort
  /** #408 — may this person see the Suspicious level: a superuser, or someone with a beta camera. Absent = not known yet = off. */
  suspicious_beta?: boolean
  /** 409 — a superuser's default for the pose overlay switch on the event video. Absent = on. */
  pose_overlay_default?: boolean
  created_at: string
  updated_at: string
}

/** #277 — `GET /cameras/?ordering=` values, also the profile's `cameras_sort`. */
export type CamerasSort = 'latest_event' | 'number'

export interface AuthTokens {
  access: string
  refresh: string
}

export interface LoginRequest {
  username?: string
  email?: string
  password: string
  fcm_token?: string
  device_type?: 'android' | 'ios' | 'web'
  /** #240 — reCAPTCHA Enterprise token (action `login`); omitted when reCAPTCHA is off. */
  recaptcha_token?: string
}

/** #240 — GET /auth/captcha-config. `siteKey` is public; null when the mode is `off`. */
export interface CaptchaConfig {
  mode: 'off' | 'verify' | 'enforce'
  siteKey: string | null
}

export interface TwoFactorDelivery {
  channel: string
  hint: string
  sms_sent: boolean
}

export interface LoginTokensResponse {
  user: User
  tokens: AuthTokens
  device_token_created?: boolean
}

export interface LoginOtpChallengeResponse {
  otp_required: true
  challenge_token: string
  delivery: TwoFactorDelivery
}

export type LoginResponse = LoginTokensResponse | LoginOtpChallengeResponse

export function isOtpChallenge(r: LoginResponse): r is LoginOtpChallengeResponse {
  return 'otp_required' in r && r.otp_required === true
}

export interface RegisterRequest {
  username: string
  email: string
  password: string
  password_confirm: string
  first_name?: string
  last_name?: string
}

// ============================================================================
// Camera Types
// ============================================================================

export interface AudioClassOption {
  audio_class_id: number
  class_name: string
  enabled: boolean
  global_threshold: number
  threshold_override: number | null
  effective_threshold: number
  recent_scores: number[]
}

export interface AudioConfigResponse {
  camera_id: number
  audio_classes: AudioClassOption[]
}

export interface AudioConfigPatch {
  audio_class_id: number
  enabled?: boolean
  threshold_override?: number | null
}

// #410 — what a person may do beyond viewing, through a share (owners and superusers always may).
export interface ShareRights {
  delete_events: boolean
  change_settings: boolean
}

// CP 236 — the four Events tabs a camera can open on by default.
export type DefaultEventCategory = 'animal' | 'human' | 'other' | 'all'

export interface Camera {
  id: number
  /** #410 — the requester's own rights on this camera (read-only). */
  my_rights?: ShareRights
  name: string
  imei: string
  iccid: string | null
  location: string | null
  latitude: string | null
  longitude: string | null
  location_address?: string | null  // #247 — Google address (item 094); absent from an older API
  is_online: boolean | null
  // CP 189 — 'manual' pins is_online to true for HTTP cameras. Superuser-writable
  // only, and only through camerasApi.setOnlineStatusMode.
  online_status_mode: 'auto' | 'manual'
  // CP 236 — the Events tab this camera's "view events" links open on. Owner or
  // superuser writable. Optional: an older API omits it; treat absent as 'animal'.
  default_event_category?: DefaultEventCategory
  // #330 — where the camera looks (heading 0–359°, width 10–180°, range 5–500 m). Superuser-writable.
  // Optional: an older API omits them; null = not set (the map draws no wedge without a heading).
  heading_degrees?: number | null
  fov_degrees?: number | null
  view_range_m?: number | null
  // #406 — the "At the train" zone drawn over the camera's picture (superuser-writable through
  // camerasApi.setLevelZones). Optional: an older API omits it; {} = nothing drawn.
  level_zones?: LevelZones
  /** #408 — the Beta switch: on this camera the captioner judges each person's appearance. Read-only; set by a superuser (PUT …/clues-beta/). */
  clues_beta_enabled: boolean
  is_active: boolean
  ingestion_type: 'http' | 'ftp' | 'mqtt'   // 'mqtt' = G100 (CP 356)
  // #361 — the owner's user id, as the API sends it (CameraSerializer emits the
  // ForeignKey as its integer pk); name and email are user_username / user_email.
  user: number
  user_username: string
  user_email: string | null
  shared_users: User[]
  shared_users_details: Array<{
    id: number
    username: string
    email: string
    first_name: string
    last_name: string
  }>
  is_owner: boolean
  group_id: number | null
  group_name: string | null
  ai_models: number[]
  ai_model_details: Array<{
    id: number
    name: string
    description: string
  }>
  motion_detection_enabled: boolean
  night_vision_enabled: boolean
  recording_enabled: boolean
  push_enabled: boolean
  /** @deprecated CP 159 — derived from push_filters on read. Do not read and write back:
   *  a category-only filter reads back as [], and [] means "everything" on write. */
  push_class_names: string[]
  /** CP 159 — the camera's alert filter. {} means everything is pushed.
   *  Applies to EVERY user who can see this camera. */
  push_filters: PushFilters
  battery_level: number | null
  battery_updated_at: string | null
  // HTTP camera telemetry (Change #018) — populated only for ingestion_type='http'
  // cameras after they POST. FTP cameras leave these as null/empty.
  signal_level: number | null
  temperature: number | null
  sd_total_mb: number | null
  sd_used_mb: number | null
  firmware_version: string
  // #045 — cellular carrier from the camera Network field (latest heartbeat).
  network_carrier: string | null
  // HTTP camera reported settings (Change #009) — what the camera last
  // reported via /api/params. Read-only; populated by camera POST.
  pir_interval: number | null
  photo_size: string
  video_resolution: string
  video_length: number | null
  params_reported_at: string | null
  // SIM lock-in (#040). imsi is the registered/trusted value, imsi_reported is
  // the last value firmware sent (NULL for pre-#040 firmware), sim_state is
  // computed by the backend (always 'ok' if SIM_LOCKIN_ENFORCEMENT_ENABLED is
  // off — Phase 1 default). qr_code is the parking field (Code from QR).
  imsi: string | null
  imsi_reported: string | null
  sim_last_mismatch_at: string | null
  sim_mismatch_count: number
  qr_code: string | null
  sim_state: 'ok' | 'mismatched'
  is_locked: boolean  // #077 — mismatched AND viewer is non-superuser → camera locked
  // #051 — desired-vs-reported convergence: none=neutral/not reported,
  // pending=drifted settings unsent, applied=camera matches desired.
  // #396 — sent=the camera fetched the changed settings at least once; its
  // next menu report has not confirmed them yet.
  settings_state: 'none' | 'pending' | 'sent' | 'applied'
  settings_pending_count: number  // #052 — unconfirmed (drift) count, same source as settings_state; > 0 for pending AND sent
  settings_applied_at: string | null  // #052 — when the camera last reached the desired state
  settings_pending_since: string | null  // #061 — active push requested_at when pending
  settings_sent_at: string | null  // #396 — time of the last hand-over; non-null exactly when settings_state is 'sent'
  settings_pending_fields: { field: string; desired: unknown; current: unknown; sent: boolean }[]  // #106 — which settings are still pending; #396 sent=handed to the camera, awaiting confirmation
  // HTTP camera desired settings (Changes #012 + #032) — what the operator
  // wants the camera to be at. Writable. NULL/empty means no change requested.
  // Once camera /api/params reports values matching, the desired_* are
  // cleared (convergence).
  desired_pir_interval: number | null
  desired_photo_size: string
  desired_video_resolution: string
  desired_video_length: number | null
  desired_extra_settings: DesiredExtraSettings
  last_seen: string | null
  created_at: string
  updated_at: string
  // Latest detection info
  latest_detection_thumbnail: string | null
  last_detection_timestamp: string | null
  total_detections: number
  total_photos: number
  total_videos: number
}

export interface CameraCreateRequest {
  name: string
  imei: string
  iccid?: string
  location?: string
  latitude?: string | null
  longitude?: string | null
  ai_models?: number[]
  ingestion_type?: 'ftp' | 'http'
  // SIM lock-in (#040). Required for ingestion_type='http' (server-validated).
  // Captured from the QR code on the camera screen.
  imsi?: string
  qr_code?: string
}

// Bucket-2 + one-shot HTTP camera desired settings.
// All fields optional; missing key = "no change requested".
// Validated against the firmware-supported enum table on the backend;
// PATCH errors come back as 400 with field-level error messages.
// #044 Phase A: pruned to the keys the backend validator still accepts.
// Removed stale Document-A keys (video_size dup, camera_name, password,
// recycle_storage_days, format_sd_now, set_time_now, reset_defaults_now) —
// not in the Document C 35-setting spec; the encoder dropped them anyway.
// #044 Phase B added the full Document C 35-setting coverage (the 20 keys
// below the divider). Numeric ranges typed as `number` (literal unions for
// -60..60 etc. are impractical); the backend menu_txt_validators enforces
// exact bounds and returns 400 with a field error on violation.
export type AlarmFlash =
  | 'BR1' | 'BR2' | 'BR3' | 'BR4' | 'BR5' | 'BR6' | 'BR7' | 'BR8' | 'BR9'
  | 'BR10' | 'BR11' | 'BR12'
  | 'BRDOUBLE2' | 'BRDOUBLE3' | 'BRDOUBLE4' | 'BRDOUBLE5'
  | 'WHITE1' | 'WHITE2' | 'WHITE3' | 'WHITE4' | 'WHITE5' | 'WHITE6'
export type Language =
  | 'ENGLISH' | 'FINLAND' | 'GERMANY' | 'FRENCH' | 'SPAIN' | 'SWEDISH'
  | 'FINNISH' | 'NORWAY' | 'DANISH' | 'ITALIAN' | 'POLISH' | 'RUSSIAN'
  | 'JAPANESE'

export interface DesiredExtraSettings {
  send_mode?: { mode: 'instant' | 'daily'; count: number } | { mode: 'off' }
  work_hour?: string  // 'HH:MM-HH:MM' or 'off'
  pir_trigger?: 'off' | 'low' | 'normal' | 'high'
  camera_mode?: 'photo' | 'video' | 'both'
  sms_control?: 'on' | 'off'
  time_lapse?: { unit: 'off' } | { unit: 'minutes'; value: number } | { unit: 'hours'; value: number }
  work_day?: string  // 7-char string of '0'/'1', e.g. '1111111'
  attachment_size?: 'normal' | 'high' | 'low'
  gps?: 'on' | 'off'
  recycle_storage_state?: 'on' | 'off'
  // ── #044 Phase B: remaining Document C settings ──
  photo_burst?: 1 | 2 | 3
  date_type?: 'ymd' | 'mdy' | 'dmy'
  auto_sim?: 'auto' | 'manual'
  lock_net?: 'auto' | '2g' | '3g' | '4g'
  alarm_flash?: AlarmFlash
  language?: Language
  beep_sound?: 'on' | 'off'
  time_stamp?: 'on' | 'off'
  alarm_by_ai?: number          // 0..10 (0 = off, 1-10 = seconds)
  alarm_by_motion?: 'on' | 'off'
  alarm_by_cloud?: 'on' | 'off'
  alarm_sound?: number          // 0..20 seconds
  alarm_light?: number          // 0..20 seconds
  alarm_sound_volume?: 0 | 1 | 2
  uk_attach_size?: number       // 0..9
  uk_lock_menu?: 0 | 1
  heartbeat?: 0 | 1 | 2 | 3     // 0=off, 1=6h, 2=12h, 3=24h
  sms_time_delay?: 0 | 1 | 2 | 3 | 4
  time_zone?: number            // -12..12
  time_zone_offset?: number     // -60..60
}

// ============================================================================
// Admin User Management Types
// ============================================================================

export interface AdminUser {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  is_active: boolean
  is_staff: boolean
  is_superuser: boolean
  /** #194 — whether this account has a verified 2FA phone, so the reset can be offered. */
  phone_verified: boolean
  created_at: string
}

export interface AdminUserCreateRequest {
  username: string
  email: string
  password: string
  first_name?: string
  last_name?: string
  is_active?: boolean
  is_staff?: boolean
  is_superuser?: boolean
}

export interface AdminUserUpdateRequest {
  email?: string
  first_name?: string
  last_name?: string
  password?: string
  is_active?: boolean
  is_staff?: boolean
  is_superuser?: boolean
}

// #076 — SIM mismatch review (superuser). Matches /admin/cameras/sim-mismatches/.
export interface SimMismatch {
  camera_id: number
  imei: string
  name: string
  owner_username: string | null
  owner_email: string | null
  imsi_registered: string | null
  imsi_reported: string | null
  sim_last_mismatch_at: string | null
  sim_mismatch_count: number
  qr_code: string | null
}

// Matches /admin/cameras/{id}/sim-audit/ rows.
export interface SimAudit {
  id: number
  changed_at: string
  changed_by_username: string | null
  old_imsi: string | null
  new_imsi: string | null
  reason: string
}

// Lightweight type for map pins — matches /cameras/map-pins/ endpoint
export interface CameraMapPin {
  id: number
  name: string
  location: string | null
  latitude: string
  longitude: string
  is_online: boolean | null
  group_names: string[]
  /** #363 — the ids of the camera's groups, beside their names; absent from an older API */
  group_ids?: number[]
  last_event_type: string | null
  last_event_at: string | null
  is_locked: boolean  // #077 — SIM mismatch lock
  default_event_category?: DefaultEventCategory  // CP 236 — absent from an older API
  // #247 — both optional: absent from an older API, ignored by an older dashboard
  location_address?: string | null
  last_event_thumbnail_url?: string | null
  // #330 — the camera's facing wedge; absent from an older API
  heading_degrees?: number | null
  fov_degrees?: number | null
  view_range_m?: number | null
}

// #363 — a camera group's site drawing (GET /camera-groups/{id}/site-plan/) and its hand-placed pins.
/** Where a camera sits on the drawing: fractions (0..1) of its width and height, origin top left. */
export interface SitePlanPin {
  camera_id: number
  x: number
  y: number
}

export interface SitePlan {
  id: number
  /** Presigned S3 link, valid 1 hour. */
  image_url: string
  width: number
  height: number
  content_type: string
  members_can_arrange: boolean
  /** True for a superuser, or for an owner / shared user of the group when members may arrange. */
  can_arrange: boolean
  updated_at: string
  /** CP 363 amendment 2026-10-18 — the group's current cameras by name, GPS or not: the site view's camera list.
   *  Optional: absent from an API before the amendment (the view then lists the group's map pins). */
  cameras?: SitePlanCamera[]
  pins: SitePlanPin[]
}

export interface SitePlanCamera {
  id: number
  name: string
}

/** GET /site-plans/: one row per group the user can see that has a drawing. */
export interface SitePlanSummary {
  group_id: number
  group_name: string
  plan_id: number
  updated_at: string
}

// #121 — fleet-wide camera counts for the dashboard stat cards.
export interface CameraStats {
  total: number
  active: number
}

export interface SIMUsage {
  status: string
  monthUsedMB: number
  monthRemainingMB: number
  totalUsedMB: number
  packageName: string
  packageEndDate: string
  networkStatus: string
  imei: string
}

// #226 — settings a bulk group apply-settings request can carry: every
// bucket-2 (desired_extra_settings) key plus the bucket-1 typed columns
// under their API names. Task 11 adds the rest of the bulk-request shape.
export type BulkSettings = DesiredExtraSettings & {
  pir_interval?: number
  photo_size?: string
  video_resolution?: string
  video_length?: number
}

// Item 226: POST /api/camera-groups/{id}/apply-settings/ — preview (dry_run) and apply.
export interface BulkSettingsChange { requested: unknown; reported: unknown; to: unknown }
export interface BulkSettingsPreviewRow {
  camera_id: number; name: string; changes: Record<string, BulkSettingsChange>
  keeps: Record<string, unknown>; reports_back: boolean; params_reported_at: string | null
}
export type BulkSettingsReason = 'excluded_by_request' | 'not_an_http_camera' | 'already_set' | 'already_requested'
export interface BulkSettingsOtherRow { camera_id: number; name: string; reason: BulkSettingsReason }
export interface BulkSettingsGroupBlock { id: number; name: string; member_count: number }
export interface BulkSettingsPreview {
  group: BulkSettingsGroupBlock; will_change: number; already_set: number
  preview: BulkSettingsPreviewRow[]; unchanged: BulkSettingsOtherRow[]; skipped: BulkSettingsOtherRow[]
  preview_token: string; expires_in: number
}
export interface BulkSettingsApplied {
  group: BulkSettingsGroupBlock; applied: number
  results: Array<{ camera_id: number; push_row_id: number | null; desired_state: Record<string, unknown> }>
  unchanged: BulkSettingsOtherRow[]; skipped: BulkSettingsOtherRow[]
}

// Item 226 task 14: GET /api/camera-groups/{id}/settings-status/ — settings_state
// and pending_fields are the camera endpoint's settings_state / settings_pending_fields.
export interface GroupSettingsStatusCamera {
  camera_id: number; name: string; ingestion_type: string
  settings_state: Camera['settings_state']
  pending_fields: Camera['settings_pending_fields']
  params_reported_at: string | null; last_seen: string | null
}
export interface GroupSettingsStatus { group: BulkSettingsGroupBlock; cameras: GroupSettingsStatusCamera[] }

export interface CameraUpdateRequest {
  name?: string
  iccid?: string
  location?: string
  latitude?: string | null
  longitude?: string | null
  is_active?: boolean
  ai_models?: number[]
  ingestion_type?: 'ftp' | 'http'
  default_event_category?: DefaultEventCategory  // CP 236 — owner or superuser only
  // #330 — superuser only (the API refuses anyone else); null clears
  heading_degrees?: number | null
  fov_degrees?: number | null
  view_range_m?: number | null
  // HTTP config push (Change #032)
  desired_pir_interval?: number | null
  desired_photo_size?: string
  desired_video_resolution?: string
  desired_video_length?: number | null
  desired_extra_settings?: DesiredExtraSettings
}

// ============================================================================
// Camera Group Types
// ============================================================================

export interface CameraGroup {
  id: number
  name: string
  camera_count: number
  cameras_details: Array<{
    id: number
    name: string
    imei: string
    location: string | null
    latitude: string | null
    longitude: string | null
    is_online: boolean | null
    latest_detection_thumbnail: string | null
    last_detection_timestamp: string | null
    total_detections: number
  }>
  /** True when the requester owns the group; false for a recipient of a shared group (#096). */
  is_owner?: boolean
  /** Users this group is shared with — populated for the owner only (#096). */
  shared_with?: Array<{ id: number; username: string; email: string; can_delete_events: boolean; can_change_settings: boolean }>
  /** #410 — the requester's own rights on this group's cameras (read-only). */
  my_rights?: ShareRights
  /** Owner's email — the owner and superusers only; null otherwise (#152). Lets the
   *  UI tell two same-named groups apart (group names are unique per user). */
  owner_email?: string | null
  created_at: string
  updated_at: string
}

/** GET /camera-groups/{id}/detection-models/ (item 266): the add-on models a group can switch. */
export interface GroupDetectionModel {
  id: number
  name: string
  description: string
  /** How many of the group's cameras have the model on. */
  subscribed_count: number
}

export interface GroupDetectionModelList {
  group: { id: number; name: string; member_count: number }
  models: GroupDetectionModel[]
}

/** POST /camera-groups/{id}/detection-models/ (item 266). Ids ascending. */
export interface GroupDetectionModelResult {
  group: { id: number; name: string; member_count: number }
  ai_model: { id: number; name: string }
  subscribed: boolean
  dry_run: boolean
  /** Changed — or, in a dry run, would change. */
  updated: number[]
  unchanged: number[]
  /** Cameras the user may not change (not theirs, and not a superuser). */
  skipped: Array<{ id: number; reason: 'not_camera_owner' }>
}

/** GET /camera-groups/{id}/audio-classes/ (item 280): the sound-detection classes a group can switch. */
export interface GroupAudioClass {
  id: number
  class_name: string
  model_name: string
  /** The class's own threshold (0–1), used by a camera with no override. */
  global_threshold: number
  /** How many of the group's cameras have the class on. */
  enabled_count: number
}

export interface GroupAudioClassList {
  group: { id: number; name: string; member_count: number }
  classes: GroupAudioClass[]
}

/** Body of POST /camera-groups/{id}/audio-classes/ (item 280). `threshold_override` absent =
 *  each camera keeps its own; null = clear it on every camera; a number (0–1) = set it on every camera. */
export interface GroupAudioClassChange {
  audio_class_id: number
  enabled: boolean
  threshold_override?: number | null
}

/** POST /camera-groups/{id}/audio-classes/ (item 280). Ids ascending. */
export interface GroupAudioClassResult {
  group: { id: number; name: string; member_count: number }
  audio_class: { id: number; class_name: string; model_name: string; global_threshold: number }
  enabled: boolean
  threshold_override: number | null | 'unchanged'
  dry_run: boolean
  /** Changed — or, in a dry run, would change (link or threshold). */
  updated: number[]
  unchanged: number[]
}

/** GET /camera-groups/{id}/critical/ (item 294, superusers): the group's critical-alert settings. No
 *  settings yet = off at 0.40. */
export interface GroupCriticalSettings {
  group_id: number
  enabled: boolean
  /** Spray-sound score (0.10–0.99) at or above which an event is critical. */
  spray_threshold: number
  camera_count: number
  updated_at: string | null
  updated_by: string | null
  /** #406 — the group's level rules as stored: `{}` (or a partial map) = the defaults, shadow mode. Absent on an old API. */
  level_rules?: Partial<LevelRules> | Record<string, never>
}

/** Body of PUT /camera-groups/{id}/critical/ (item 294); `spray_threshold` absent = keep the current one.
 *  #406 — `level_rules` absent = keep the current rules. */
export interface GroupCriticalSettingsChange {
  enabled: boolean
  spray_threshold?: number
  level_rules?: LevelRules
}

/** PUT /camera-groups/{id}/critical/ (item 294): the settings + the cameras that get (or, in a dry run,
 *  would get) the Graffiti sound (Aerosol_spray) switched on — 0 when turning off. #406 — a dry run with
 *  `level_rules` also answers the 30-day replay of those rules (`preview`). */
export interface GroupCriticalSettingsResult extends GroupCriticalSettings {
  audio: { updated: number; unchanged: number }
  preview?: LevelPreview
}

/** POST /camera-groups/{id}/default-events-view/ (item 238). Ids ascending. */
export interface GroupDefaultEventsViewResult {
  group: { id: number; name: string; member_count: number }
  default_event_category: DefaultEventCategory
  dry_run: boolean
  /** Changed — or, in a dry run, would change. */
  updated: number[]
  unchanged: number[]
  /** Cameras the user may not change (not theirs, and not a superuser). */
  skipped: Array<{ id: number; reason: 'not_camera_owner' }>
}

export interface CameraGroupCreateRequest {
  name: string
  cameras?: number[]
}

// ============================================================================
// Event Types
// ============================================================================

export interface VisualDetection {
  class_name: string
  confidence: number
  bbox:
    | { type: 'standard'; x1: number; y1: number; x2: number; y2: number }
    | { type: 'obb'; corners: Array<{ x: number; y: number }> }
}

export interface AudioDetection {
  class_name: string
  confidence: number
  threshold: number
  chunk_index: number
  time_start: number
  time_end: number
}

/** 409 — pose review: the stored skeletons of an event, and what superusers decided about the clue. */
export type ReviewVerdict = 'graffiti' | 'hand_not_graffiti' | 'no_hand'
export interface PoseFrame { f: number; p: Array<{ t: number; k: Array<[number, number, number]> }> }
export interface HandAtTrain {
  state: 'yes' | 'no' | 'not_assessed'
  reason: string | null
  start_s: number | null
  end_s: number | null
  swings: number | null
  track: number | null
  box_kind: 'vehicle' | 'train' | null
  /** 409 — wrist at a vehicle box: true / false, null when the clip has no vehicle box or no chain (older rows lack it). */
  at_vehicle?: boolean | null
}
export interface EventPose {
  model_name: string
  model_version: string
  fps: number
  frame_width: number
  frame_height: number
  frame_stride: number
  frames: PoseFrame[]
  hand_at_train: HandAtTrain
  reviews: Array<{ user: string; verdict: ReviewVerdict; updated_at: string }>
  my_verdict: ReviewVerdict | null
}
export interface ReviewCounts { graffiti: number; hand_not_graffiti: number; no_hand: number; disputed: number; unreviewed: number }
export interface ReviewSummary { clue: string; by_state: Record<'yes' | 'no' | 'not_assessed', ReviewCounts> }

export interface Event {
  id: number
  /** #410 — whether the requester may delete this event (never a Critical one through a share). */
  can_delete?: boolean
  /** 409 — true only for a superuser on an event that has a stored pose. */
  pose_available?: boolean
  // Item 409 — superuser on a Beta camera: a pose row may still arrive (the modal polls only then)
  pose_expected?: boolean
  camera: number
  camera_name: string
  camera_group: string
  media: number | null
  media_details: Media | null
  event_type: string
  description: string
  activity_caption: string | null // #109 one-sentence Qwen activity caption; null when none
  confidence_score: number
  metadata: Record<string, unknown>
  thumbnail_s3_key: string | null
  thumbnail_url: string | null
  processed_video_s3_key: string | null
  detection_video_url: string | null
  video_status?: 'processing' | 'ready' | 'failed' | null // #332 — the playable copy of a video: being made, ready, or failed (never coming); null for images and events older than #332
  detected_objects: string[]
  audio_detections: AudioDetection[]
  is_read: boolean
  is_favorite: boolean // #122 — whether the current user has favourited this event
  is_unread?: boolean // #292 — the current user has not opened this event yet (per person)
  is_motion_only?: boolean // #304 — nothing recognised, no sound: left out of the dashboard's Recent Events + unread card
  is_critical?: boolean // #294 — a critical event (core: spray sound above the group's threshold)
  critical_reasons?: CriticalReason[] // #294 — the evidence, e.g. [{code: 'spray_sound', score: 0.642, threshold: 0.4}]
  acknowledged_by?: string | null // #294 — who acknowledged it (name; null when not, or the user was deleted)
  acknowledged_at?: string | null // #294 — when (ISO 8601); null = still open
  critical_age_seconds?: number | null // #294 redesign — whole seconds since created_at by the server's clock (critical only; missing on an old API)
  _seeded?: boolean // #294 redesign — client only: a placeholder made from a WebSocket payload until the list is fetched
  // #406 — the event's level: recent (today's normal event), suspicious (a person plus enough clue points under the
  // rules of the camera's group) or critical (spray sound; always equals is_critical). Missing on an old API.
  level?: EventLevel
  computed_level?: EventLevel // #406 — the strictest level over every group with rules, shadow groups included
  clue_results?: ClueResult[] // #406 — the clue answers, with plain words in evidence.summary for the ones that said yes
  seen_by?: string | null // #406 — who pressed Seen on a suspicious event (name; null when nobody, or the user was deleted)
  seen_at?: string | null // #406 — when (ISO 8601); null = still open (not yet seen)
  seen_reason?: 'worker' | 'unsure' | 'real' | null // #406 — the optional reason given with Seen
  category?: 'animal' | 'human' | 'other' | 'important' // #124 — server-derived event tab
  occurred_at: string
  created_at: string
  // #094 — frozen location snapshot at capture time (null on pre-#094 events)
  latitude: string | null
  longitude: string | null
  location_address: string | null
}

/** #294 — the spray sound made the event critical. */
export interface SprayCriticalReason {
  code: 'spray_sound'
  /** The video's highest score for the sign (0–1, 3 decimals). */
  score: number
  /** The camera's critical threshold it was compared with (0–1). */
  threshold: number
}

/** #408 — the appearance clues made the event critical, on a beta camera. */
export interface CluesCriticalReason {
  code: 'clues'
  /** The clue codes that scored. */
  clues: string[]
}

/** #294 — why an event is critical. */
export type CriticalReason = SprayCriticalReason | CluesCriticalReason

/** #406 — the three event levels. `critical` is today's spray-sound alarm (`is_critical`), unchanged. */
export type EventLevel = 'recent' | 'suspicious' | 'critical'

/** #406 — one clue specialist's answer on an event (the task handler's `core/level`). `evidence.summary` carries
 *  the plain words the dashboard shows ("standing 3 min"); the rest of `evidence` is the specialist's own. */
export interface ClueResult {
  code: string
  state: 'yes' | 'no' | 'not_assessed'
  /** Calibrated 0–1 where meaningful, else null. */
  score: number | null
  evidence: { summary?: string; [k: string]: unknown }
  /** The specialist's version, e.g. 'still/1'. */
  version: string
}

/** #406 — a clue's weight in a group's rules: Off = 0 points, Weak = 1, Strong = 2. */
export type ClueWeight = 'off' | 'weak' | 'strong'

/** #406 — the v1 clues that can score (the person gate and the spray-sound override are not in `clues`). */
export type ScoringClueCode = 'still' | 'staying' | 'at_train'
/** #408 — judged per person by the captioner, only on a camera with the Beta switch on; the sheet tags them Beta. */
export type AppearanceClueCode = 'no_hivis' | 'backpack' | 'hoodie' | 'mask' | 'spray_can'
/** #406 — the clues registered for later items; they always answer not assessed and show locked in the sheet. */
export type LockedClueCode = 'running'

/** #406 — a group's level rules (`camera_group_critical_settings.level_rules`, schema version 1). The server
 *  accepts a partial map (missing keys take the defaults); the dashboard always sends the full normalised one. */
export interface LevelRules {
  version: 1
  /** Shadow mode while false: levels are computed and stored, nothing is shown or pushed. */
  suspicious_enabled: boolean
  clues: Record<string, ClueWeight>
  bars: { suspicious: number; critical: number }
  person_derived_cap: number
  params: {
    still: { min_seconds: number; max_body_lengths_per_s: number }
    staying: { min_seconds: number; min_uploads: number; window_minutes: number }
    at_train: { min_frames: number }
  }
}

/** #406 — the 30-day replay of candidate rules over the group's stored clue answers (PUT …/critical/ dry_run). */
export interface LevelPreview {
  days: Array<{ date: string; suspicious: number; critical: number; recent: number }>
  cameras: Array<{ camera_id: number; name: string; suspicious: number; critical: number }>
  total_events: number
  /** Rows from before the feature (no clue answers): the preview cannot count them. */
  events_without_clues: number
  /** The group had more than 20 000 events in the window: one row in this many per day was replayed. */
  sampled?: number
}

/** #406 — a camera's drawn zones (`cameras.level_zones`): points normalised 0–1 over the picture, 3–8 of them. */
export interface LevelZone {
  points: Array<[number, number]>
  frame?: [number, number]
  set_by?: number | null
  set_at?: string | null
}
export interface LevelZones {
  at_train?: LevelZone | null
}

/** #406 — body of PUT /cameras/{id}/level-zones/: the zone to store (3–8 points, 0–1), or null to clear it. */
export interface LevelZoneChange {
  at_train: { points: Array<[number, number]>; frame?: [number, number] } | null
}

/** #413 — which chime: the Regular three rising notes, or the gentle two falling notes of a Suspicious event. */
export type ChimeKind = 'regular' | 'suspicious'
/** #294 redesign — the critical sounds the super admin can pick (Settings → System). #338: 'client' = the
 *  client's own recording (a file, 5.06 s), the default; the other three are synthesized. */
export type CriticalSoundKind = 'client' | 'hazard' | 'siren' | 'horn'

/** #294 redesign — GET/PATCH /system-settings/critical-sound/: the super admin's critical sound rules for
 *  every dashboard. repeat_seconds 0 = once only; stop_after_seconds 0 = until acknowledged. */
export interface CriticalSoundSettings {
  sound: CriticalSoundKind
  repeat_seconds: 0 | 5 | 10 | 30 | 60
  stop_after_seconds: 60 | 300 | 600 | 0
}

/** #294 redesign — what a dashboard uses when it cannot read the rules (loading, old API, error). */
export const DEFAULT_CRITICAL_SOUND_SETTINGS: CriticalSoundSettings = {
  sound: 'client',   // #338 — the client's recording
  repeat_seconds: 10,
  stop_after_seconds: 300,
}

/** #294 — POST /events/acknowledge-all/: the ids acknowledged now (open, critical, visible to the user). */
export interface AcknowledgeAllResult {
  count: number
  event_ids: number[]
}

export type EventType =
  | 'person_detected'
  | 'dog_detected'
  | 'car_detected'
  | 'motion_detected'
  | 'sound_detected'

// ============================================================================
// Media Types
// ============================================================================

export interface FullSizeRequest {
  status: 'waiting' | 'arrived' | 'failed'
  requested_at: string
  reason: string
}

export interface Media {
  id: number
  camera: number
  camera_name: string
  media_type: 'video' | 'image'
  media_s3_key: string
  thumbnail_s3_key: string | null
  file_url: string | null
  thumbnail_url: string | null
  duration: number | null
  file_size: number
  resolution: string | null
  event_type: string | null
  tags: string[]
  captured_at: string
  created_at: string
  camera_file_name: string | null
  full_size_url: string | null
  can_request_full_size: boolean
  full_size_request: FullSizeRequest | null
}

// ============================================================================
// Notification Types
// ============================================================================

export interface Notification {
  id: number
  event: number | null
  title: string
  message: string
  notification_type: string
  is_read: boolean
  is_sent: boolean
  created_at: string
}

export interface NotificationSettings {
  push_enabled: boolean
  push_motion_detection: boolean
  push_person_detection: boolean
  push_vehicle_detection: boolean
  email_enabled: boolean
  email_motion_detection: boolean
  email_person_detection: boolean
  sound_enabled: boolean
}

// ============================================================================
// AI Model Types
// ============================================================================

export interface AIModel {
  id: number
  name: string
  base_model: number
  base_model_name: string
  target_classes: number[]
  target_class_details: DetectionClass[]
  description: string
  version: string
  is_active: boolean
}

export interface DetectionClass {
  id: number
  class_id: number
  class_name: string
  category: string
  base_model: number
}

// Payload to add a species to an open-vocabulary (speciesnet-bioclip) catalog (#108).
// class_id is auto-assigned server-side.
export interface DetectionClassCreate {
  base_model: number
  class_name: string
  category?: string
}

export interface AvailableClass {
  class_id: number
  class_name: string
  category: string
  global_confidence?: number | null     // global per-class default (Phase 1)
  camera_override?: number | null        // this camera's per-class override
  effective_confidence?: number          // resolved value for display
}

export interface DetectorConfigOption {
  ai_model_id: number
  ai_model_name: string
  ai_model_is_active: boolean
  subscribed: boolean
  base_model_id: number | null
  base_model_name: string | null
  base_model_type?: string | null        // 'speciesnet-bioclip' => owner per-species enable/disable (#108)
  inherited_confidence: number | null
  confidence_override: number | null
  class_override_enabled: boolean
  active_class_ids: number[]
  inherited_class_ids: number[]
  disabled_classes?: number[]            // per-camera disabled species (relabel-capable models, #108)
  available_classes: AvailableClass[]
}

export interface DetectorConfigsResponse {
  camera_id: number
  ensemble_enabled: boolean
  detectors: DetectorConfigOption[]
}

export interface DetectorConfigPatch {
  ai_model_id: number
  subscribed?: boolean
  confidence_override?: number | null
  class_override_enabled?: boolean
  class_override_ids?: number[]
  class_thresholds?: Record<string, number | null>
  disabled_classes?: number[]            // owner-writable per-camera species disable (#108)
}

export interface GlobalThresholdClass {
  class_id: number
  class_name: string
  category: string
  global_confidence: number | null
}

export interface GlobalThresholdModel {
  ai_model_id: number
  ai_model_name: string
  base_model_id: number
  base_model_name: string
  base_model_type?: string               // 'speciesnet-bioclip' => editable open-vocab catalog (#108)
  base_default: number
  classes: GlobalThresholdClass[]
}

export interface GlobalThresholdsResponse {
  models: GlobalThresholdModel[]
}

export interface GlobalThresholdPatch {
  base_model_id: number
  class_id: number
  confidence: number | null
}

// ============================================================================
// API Response Types
// ============================================================================

export interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

export interface ApiError {
  detail?: string
  message?: string
  errors?: Record<string, string[]>
}

// ============================================================================
// Filter Types
// ============================================================================

export interface EventFilters {
  camera?: number
  event_type?: EventType
  start_time?: string
  end_time?: string
  detected_object?: string
  media_type?: 'video' | 'image'
  favorite?: boolean // #122 — Favourites feed: only the current user's favourited events
  unread?: boolean // #292 — "Unread only": events the current user has not opened
  hide_motion?: boolean // #304 — leave motion-only events out (the dashboard's Recent Events)
  page_size?: number // #304 — Total Events asks for 1 row: it only needs the count
  critical?: 'open' | 'all' // #294 — open (unacknowledged) critical events; #392 — 'all': every critical event, open and
  // acknowledged (the Events page's Critical tab). Both: superusers get every group's
  category?: 'animal' | 'human' | 'other' | 'important' // #124 — Animal/Human/Other tabs (omit for All)
  level?: EventLevel // #406 — events of one level (the Suspicious tab, the dashboard block); superusers get every group's
  seen?: 'open' | 'all' // #406 — with level=suspicious only: 'open' = nobody has pressed Seen yet
}

export interface MediaFilters {
  camera_id?: number
  event_type?: string
  date_from?: string
  date_to?: string
  tags?: string
}

// ============================================================================
// Notification Contact Types
// ============================================================================

export type ContactChannel = 'email' | 'sms'

export interface NotificationContact {
  id: number
  channel: ContactChannel
  contact_value: string
  first_name: string
  last_name: string
  label: string
  is_verified: boolean
  /** Item 315 — only critical (graffiti-spray) alerts are emailed/texted to this contact. */
  critical_only: boolean
  linked_camera_ids: number[]
  created_by: string | null
  is_own: boolean
  created_at: string
}

export interface NotificationContactCreate {
  channel: ContactChannel
  contact_value: string
  first_name?: string
  last_name?: string
  label?: string
}

export interface NotificationContactUpdate {
  first_name?: string
  last_name?: string
  label?: string
  critical_only?: boolean
}

/** What PATCH /notification-contacts/{id}/ answers: the Update serializer's fields only, not a full contact.
 *  Refetch the contacts for anything else. */
export interface NotificationContactUpdateResponse {
  first_name: string
  last_name: string
  label: string
  critical_only: boolean
}

export interface ContactCameraLink {
  id: number
  camera_id: number
  camera_name: string
  alert_class_names: string[]
  created_at: string
}

// ============================================================================
// Test Camera Types (camera_test app — admin only)
// ============================================================================

export interface TestCamera {
  imei: string
  firmware_version: string
  first_seen: string
  last_seen: string
  pir_interval: number | null
  photo_size: string
  video_resolution: string
  video_length: number | null
  upload_count: number
  latest_battery: number | null
  latest_signal: number | null
  latest_upload: {
    id: number
    media_type: 'image' | 'video'
    original_filename: string
    server_time: string
  } | null
}

export interface TestMediaUpload {
  id: number
  media_type: 'image' | 'video'
  original_filename: string
  file_size: number | null
  s3_key: string
  url: string | null
  signal: number | null
  battery: number | null
  temperature: number | null
  sd_total_mb: number | null
  sd_used_mb: number | null
  longitude: string | null
  latitude: string | null
  camera_time: string | null
  server_time: string
}

export interface TestCameraStatus {
  id: number
  signal: number | null
  battery: number | null
  temperature: number | null
  sd_total_mb: number | null
  sd_used_mb: number | null
  longitude: string | null
  latitude: string | null
  camera_time: string | null
  server_time: string
}

export interface TestCameraDetail {
  camera: TestCamera
  uploads: TestMediaUpload[]
  statuses: TestCameraStatus[]
}

// ============================================================================
// Annotation Tool Types
// ============================================================================

export interface AnnotationClassDef {
  id: number
  name: string
  color: string
}

export interface AnnotationProject {
  id: number
  name: string
  description: string
  class_set: AnnotationClassDef[]
  task_count: number
  completed_count: number
  annotation_count: number
  created_by: number | null
  created_by_username: string | null
  created_at: string
  updated_at: string
}

export interface AnnotationBBox {
  id?: number
  class_id: number
  class_name: string
  x_center: number
  y_center: number
  width: number
  height: number
  confidence: number | null
  source: 'auto' | 'manual' | 'corrected'
  created_by?: number | null
  created_at?: string
}

export interface AnnotationTask {
  id: number
  project: number
  s3_key: string
  image_width: number | null
  image_height: number | null
  source_type: 'detection' | 'upload' | 'test'
  source_event_id: number | null
  source_media_id: number | null
  camera_imei: string
  captured_at: string | null
  assigned_to: number | null
  status: 'pending' | 'in_progress' | 'completed' | 'reviewed'
  annotation_count?: number
  image_url?: string | null
  created_at: string
  updated_at?: string
}

export interface AnnotationTaskDetail extends AnnotationTask {
  annotations: AnnotationBBox[]
  image_url: string | null
}

export interface ImportFilters {
  cameras?: number[]
  date_from?: string | null
  date_to?: string | null
  classes?: string[]
  min_confidence?: number
  include_all_boxes?: boolean
}

export interface ImportResult {
  imported_tasks: number
  imported_annotations: number
  skipped_duplicates: number
  class_set: AnnotationClassDef[]
}

export interface ImportPreview {
  total_matching: number
  new_images: number
  already_imported: number
}

// #224 — G100 communication log: every exchange a G100 has with our servers
export type G100LogChannel =
  | 'bootstrap' | 'certificate' | 'connection' | 'mqtt' | 'upload' | 'ingest'

export interface G100Log {
  id: number
  created_at: string
  imei: string
  device: number | null
  channel: G100LogChannel
  direction: 'in' | 'out'
  ok: boolean | null
  remote_addr: string
  summary: string
  topic: string
  payload: string
}

// #309 — G100 Test Console (stage only): one owner's G100 cameras, any protocol action, the log
export type G100Presence = 'online' | 'asleep' | 'overdue' | 'never'

export interface G100ConsoleCamera {
  id: number
  name: string
  imei: string
  presence: G100Presence
  is_owner: boolean
}

// #397 — add a device from the console: IMEI, name and region; the server makes the Camera, the
// G100Device and the broker login, and tells which provisioning step (if any) failed.
export interface G100AddDeviceRequest { imei: string; name: string; region: 1 | 4 }
export interface G100AddDeviceResult {
  camera: { id: number; name: string; imei: string }
  created: boolean          // false on the same owner's repeat (provisioning re-run)
  warning: string | null    // the provisioning step that failed; the rows are in place
}

export interface G100ConsoleAction {
  code: string
  section: string
  params: Record<string, unknown>
  from_example: boolean   // the protocol's own example; otherwise its parameter table
  supported: boolean      // the console offers it
  // why not: the device service cannot send it yet, or it comes with live streaming (item 311)
  unavailable: 'device_service' | 'live_streaming' | null
  dangerous: boolean      // the camera name must be typed
  no_result: boolean      // the camera sends no action result
  server_built: boolean   // params built by the server (syncSetting)
  struck: boolean         // struck through in V1.1 and V1.2 (not on the G100); still sendable (#358)
}

export interface G100DailyReport {        // 6.14.1 names kept, as the server sends them
  received_at: string
  triggerTimes: number | null
  sendThumbnailSuccTimes: number | null
  sendThumbnailErrTimes: number | null
  sendHDSuccTimes: number | null
  sendHDErrTimes: number | null
}

export interface G100ConsoleStatus {
  presence: G100Presence
  is_online: boolean
  connected: boolean
  work_mode: number | null
  report_interval_hours: number
  last_seen_at: string | null
  next_checkin_expected: string | null
  last_login_at: string | null
  battery: number | null
  sd_capacity_mb: number | null
  sd_free_mb: number | null
  firmware: string | null
  module_firmware: string | null
  display_version: string | null
  model: string | null
  daily_report: G100DailyReport | null
  signal_percent: number | null
  reported: Record<string, unknown>
  reported_at: Record<string, string>
}

export interface G100ConsoleInfo {
  camera: { id: number; name: string; imei: string; is_owner: boolean }
  status: G100ConsoleStatus
  wake_topic: string
  action_topic: string
  actions: G100ConsoleAction[]
}

export type G100CommandState = 'pending' | 'sent' | 'done' | 'failed' | 'expired' | 'superseded'

export interface G100ConsoleCommand {
  id: number
  action: string
  params: Record<string, unknown>
  state: G100CommandState
  open: boolean           // still waits to be sent or answered
  result_code: number | null
  result: Record<string, unknown> | null
  created_at: string
  sent_at: string | null
  answered_at: string | null
}

// #379 — the console's Settings card: the complete set the next change starts from and where it
// comes from ('ours' when we hold a complete set, else the camera's complete report, else the
// server defaults), our version and whether the camera has acknowledged it, what the camera
// reported at login, and the latest syncSetting command.
export type G100SettingsSource = 'ours' | 'report' | 'defaults'

// #403 — the owner API's status (GET /api/cameras/{id}/g100/) and settings change answer; only the parts the
// customer settings sheet reads.
// #411 — plus the presence, the camera's status report and the recent actions (the Status and actions sheet).
export interface G100OwnerStatus {
  camera_id: number
  imei: string
  presence?: G100Presence
  connected: boolean
  is_online: boolean                 // online or asleep
  work_mode: number | null
  report_interval_hours: number
  last_seen_at: string | null
  next_checkin_expected: string | null
  signal_percent: number | string | null      // the report's signalStrength, as the camera sent it
  status: Record<string, unknown>              // the merged status report, field by field, untyped (6.14.3)
  status_reported_at: Record<string, string>   // each status field's own report time
  offline_keepalive_seconds: number | null
  recent_actions: G100OwnerAction[]
  can_control: boolean
  /** #410 — the requester may change picture and video settings (owner, superuser or a ticked sharee). */
  can_change_settings: boolean
  /** #410 — the action names this requester may send. */
  allowed_actions: string[]
  settings: {
    values: Record<string, unknown>
    source?: G100SettingsSource   // #404 — where `values` come from; missing on an older API = ours
    version: number
    pending: boolean
    reported: Record<string, unknown>
    reported_at: string | null
    editable: boolean
  }
}

export interface G100OwnerSettingsChange {
  version: number
  pending: boolean
  values: Record<string, unknown>
}

// #411 — the owner API's actions (GET and POST /api/cameras/{id}/g100/actions/): the six an owner may send.
export type G100OwnerActionCode = 'captureImg' | 'pauseCapture' | 'unPauseCapture' | 'updateGPS' | 'reboot' | 'formatSDCard'

export interface G100OwnerAction {
  id: number
  action: string            // any command code in the history, not only the six
  params: Record<string, unknown>
  state: G100CommandState
  result_code: number | null
  result: Record<string, unknown> | null
  created_at: string
  sent_at: string | null
  answered_at: string | null
}

export interface G100OwnerActionRequest {
  action: G100OwnerActionCode
  minutes?: number          // pauseCapture: a whole number, 1 or more
  confirm_name?: string     // formatSDCard: the camera's name, exactly
}

export interface G100ConsoleSettings {
  values: Record<string, unknown>
  source: G100SettingsSource
  version: number
  pending: boolean
  reported: Record<string, unknown>
  reported_at: string | null
  last_sync: G100ConsoleCommand | null
}

export interface G100ConsoleSettingsChange {
  version: number
  pending: boolean
  values: Record<string, unknown>
  sync: G100ConsoleCommand | null
}

export interface G100ConsoleSendRequest {
  action: string
  params: Record<string, unknown>
  confirm_name?: string
}

// #399 — stored firmware files the Firmware card sends to a camera (otaUpdate, V1.5).
export type G100FirmwareChannel = 1 | 2 | 3

export interface G100Firmware {
  id: number
  channel: G100FirmwareChannel
  version: string
  file_name: string
  size: number
  sha256: string
  uploaded_at: string
  uploaded_by: string | null
  note: string
}

export interface G100FirmwareUpload {
  file: File
  channel: G100FirmwareChannel
  version: string
  note: string
}

export interface G100ConsoleMessage {
  id: number
  created_at: string
  channel: G100LogChannel
  direction: 'in' | 'out'
  ok: boolean | null
  summary: string
  topic: string
  payload: string         // secrets masked by the server
  remote_addr: string
}

// #310 — G100 audio (owner API at /cameras/<id>/g100/audio/): the owner's audio library, the
// camera's own reported list and the four audio commands (V1.1 6.12.20-22, V1.4 6.12.23 #380). Owner or superuser only.

/**
 * V1.3 6.14.4 `speciesName`, the 29 categories in the document's order (server: settings_sync.SPECIES).
 * V1.3 renamed "Buck" to "Male Deer" and "Doe" to "Female Deer" (#370); the server still takes the old names.
 */
export const G100_SPECIES = [
  'Person', 'Vehicle', 'Male Deer', 'Female Deer', 'Giraffe', 'Bear', 'Raccoon', 'Boar', 'Horse', 'Zebra', 'Turkey',
  'Cow', 'Sheep', 'Wolf', 'Fox', 'Squirrel', 'Skunk', 'Dog', 'Cat', 'Rabbit', 'Bird', 'Elephant',
  'Badger', 'Chicken', 'Rodent', 'Hedgehog', 'Porcupine', 'Duck', 'Marten',
] as const
export type G100Species = (typeof G100_SPECIES)[number]

export interface G100AudioFile {
  audio_id: number        // the same on every camera of this owner, never reused
  file_name: string       // the name the camera stores it under
  original_name: string   // the uploaded name
  file_size: number       // bytes, exactly what the camera downloads (fileSize)
  checksum: string        // CRC32 of those bytes, 8 lowercase hex digits
  sample_rate: number
  sample_rate_ok: boolean // at the camera's rate (the limits); otherwise listed but never sent (#358)
  duration_seconds: number
  created_at: string
}

/** What the camera last reported in its status (V1.1 6.14.3 audioCount + audioList). */
export interface G100AudioOnCamera {
  reported: boolean
  audio_count: number | null
  audio_list: Record<string, unknown>[]   // the camera's own entries, as it sent them
  reported_at: string | null
  package_status: string | null           // V1.5 audio package status the camera reported
  package_status_at: string | null
}

export interface G100AudioLimits {
  max_bytes: number
  max_seconds: number
  min_sample_rate: number
  max_sample_rate: number
  channels: number
  bits_per_sample: number
  max_files: number
  max_per_command: number
}

/** One slot of the default audio package (V1.3 section 11). */
export interface G100AudioPackageSlot {
  slot: number
  species: string
  name: string
  size: number
  duration_ms: number
  source: 'tone' | 'file'
}

export interface G100AudioPackage {
  files: number
  bytes: number
  updated_at: string | null
  slots: G100AudioPackageSlot[]
}

/** #383: the answer to loading files into the default package from the console. */
export interface G100PackageLoadResult {
  stored: number[]
  removed: number[]
  ignored: string[]                 // the first 20 names in a tar that are no package file
  ignored_count: number
  warning: string | null            // set when the package is now above the factory's 15 MB
  package: G100AudioPackage
}

export interface G100AudioOverview {
  library: G100AudioFile[]
  on_camera: G100AudioOnCamera
  limits: G100AudioLimits
  package: G100AudioPackage
}

/** audioDownload: files named by audioId; the server fills fileName, downloadUrl, fileSize, checksum. */
export interface G100AudioDownloadRequest {
  overwrite?: boolean
  audioList: { audioId: number; speciesName?: G100Species }[]
}

/** audioDelete, V1.1 6.12.21 inputParams. */
export interface G100AudioDeleteRequest {
  force?: boolean
  audioList: { audioId: number; fileName?: string }[]
}

/** audioSpeciesSet, V1.1 6.12.22 inputParams. */
export interface G100AudioSpeciesRequest {
  audioList: { audioId: number; speciesName: G100Species }[]
}

/** audioPlayback, V1.4 6.12.23 inputParams (#380): type 1 plays the sound with this audioId once,
 *  type 0 turns the current warning sound off. The camera answers {status: 0 | 1 | 2}. */
export interface G100AudioPlaybackRequest {
  type: 0 | 1
  audioId: number
}

/** The queued command, as the action history shows it. */
export type G100AudioCommand = Omit<G100ConsoleCommand, 'open'>

// #059 — camera<->server exchange log (superuser source-of-truth viewer)
export interface CameraExchangeLog {
  id: number
  received_at: string
  imei: string
  camera: number | null
  camera_name: string | null
  kind: string
  method: string
  path: string
  status_code: number | null
  client_ip: string
  user_agent: string
  request_summary: string
  menu_present: boolean
  menu_content: string
  response_body: string
  server_context: Record<string, unknown> | null
  request_bytes: number
  response_bytes: number
  latency_ms: number | null
  created_at: string
}

// ============================================================================
// Google Maps Platform (#080)
// ============================================================================

export type MapProvider = 'google' | 'leaflet'
export type MapPlatform = 'web' | 'ios' | 'android'

export interface MapConfig {
  provider: MapProvider
  platform: MapPlatform
  key: string
  default_center: { lat: number; lng: number }
  default_zoom: number
  region_code: string
  language_code: string
  /** #287 — the company's map settings; partial or missing on an older API (see lib/mapSettings). */
  map_settings?: Partial<MapSettings>
}

// #287 — map settings a superuser sets for the whole company (one SystemSetting row on the API)
export type PinShape = 'round' | 'teardrop' | 'dot' | 'tag' | 'lollipop' | 'camera'
export type PinColour = 'yellow' | 'green' | 'blue' | 'orange' | 'white' | 'grey'
export type PinSize = 'small' | 'medium' | 'large'
export type OfflineMark = 'red_dot' | 'red_pin' | 'none'
export type MapStyle = 'hybrid' | 'satellite' | 'roadmap' | 'terrain'
/** #330 round 2 — the colour of the camera direction wedges; 'pin' = the pin colour. */
export type WedgeColour = 'red' | 'pin' | 'white' | 'dark_red'
export interface MapSettings {
  pin_shape: PinShape
  pin_colour: PinColour
  pin_size: PinSize
  offline_mark: OfflineMark
  show_number: boolean
  map_style: MapStyle
  street_view: boolean
  /** #330 round 2 — absent from an older API: withDefaults fills 'red'. */
  wedge_colour: WedgeColour
  /** #339 — the width (degrees, 10–180) and the range (m, 5–500) a camera with none of its own is drawn with, and
   *  the editors' placeholders; absent from an older API: withDefaults fills 40 and 10. */
  wedge_fov_degrees: number
  wedge_range_m: number
}

export interface ReverseGeocodeResult {
  lat: number
  lng: number
  formatted_address: string
  place_id: string
}

// ============================================================================
// Push notification categories (#138)
// ============================================================================

/**
 * An event's push categories. A superset of the event-page tabs: `other` means
 * exactly what the tabs mean (not animal, not human), while `vehicle` and `audio`
 * are ADDITIVE tags — a vehicle-only event is both `vehicle` and `other`.
 */
export type PushCategory = 'animal' | 'human' | 'vehicle' | 'audio' | 'other'

/**
 * #139 — the raw WebSocket event-notification payload the task handler broadcasts
 * (`database.py::broadcast_notification`, ~line 911). This is NOT a full `Event` —
 * it's the lightweight dict built for the live socket message, and it differs from
 * `Event` in two load-bearing ways:
 *   - there is no `audio_detections`; audio classes travel as `audio_class_names`
 *     (`string[]`, not `AudioDetection[]`).
 *   - `categories` is the server-authoritative output of the handler's
 *     `push_categories()` — prefer it over any client-side re-derivation.
 * `categories`/`audio_class_names` are typed optional defensively (a stale handler
 * build, a hand-built test message); callers must not assume they are always set.
 */
export interface WsEventPayload {
  id: number
  camera_id: number
  camera_name: string
  camera_group: string
  event_type: string
  description: string
  confidence_score: number
  occurred_at: string | null
  thumbnail_url: string | null
  detected_objects: string[]
  audio_class_names?: string[]
  categories?: PushCategory[]
  is_critical?: boolean // #294 — a critical event passes every dashboard mute
  critical_reasons?: CriticalReason[] // #294
  level?: EventLevel // #406 — a suspicious event takes the normal path (toast + one chime, mutes apply) and seeds the block
  clue_results?: ClueResult[] // #406
}

/**
 * #139 — the per-category push filter. Nothing is inferred from emptiness:
 *   enabled + no classes  -> the whole category (including classes added later)
 *   enabled + classes     -> only those classes, within that category
 *   disabled              -> nothing from this category, whatever `classes` says
 *   every category off    -> NO push. Muting is also available globally via
 *                            `push_enabled` on /notifications/settings/.
 * GET always returns all five categories, so the UI never has to guess a default.
 */
export interface PushCategoryFilter {
  enabled: boolean
  classes: string[]
}

/** #406 — beside the five categories, the `suspicious` switch as a filter map STORES it (a camera row of GET
 *  /push-preferences/cameras/): false = no Suspicious alerts (phone and bell); absent = on. Independent of the
 *  categories; it never bypasses the camera's switch or filter. The account's answer has it next to `filters`
 *  (PushPreferences.suspicious). */
export type PushFilters = Record<PushCategory, PushCategoryFilter> & { suspicious?: boolean }

export interface PushPreferences {
  filters: PushFilters
  /** #406 — the Suspicious switch, NEXT TO `filters` on the account's GET and PUT (today's phone app reads every value
   *  inside `filters` as a category). Always in the answer; absent from an older API = on. Left out of a PUT, the
   *  stored value is kept. */
  suspicious?: boolean
}

// ============================================================================
// CP 391 — "My phone alerts": each person's own push choice per camera
// ============================================================================

/**
 * The modes the API speaks (the app and the dashboard never send category filters for this). #406 — mode 3
 * `suspicious` ("Suspicious + graffiti") = the categories off and the Suspicious switch on; `critical_only` ("Only
 * graffiti") now writes the switch off. `account` = no camera row (the person's account setting);
 * `custom` = a camera row this item did not write (read only; none exist when it ships).
 */
export type MyPushMode = 'account' | 'all' | 'categories' | 'suspicious' | 'critical_only' | 'custom'

/** What a person can choose (never `custom`). */
export type MyPushChoice = Exclude<MyPushMode, 'custom'>

/** The three ticks under "Humans / Animals / Vehicles". */
export type MyPushCategory = 'human' | 'animal' | 'vehicle'

/** Everything outside the box that changes what arrives (read-only; set by the camera owner or the person). */
export interface MyPushLimits {
  /** The camera owner's push switch for this camera (CP 164). */
  camera_push_enabled: boolean
  /** Which of human / animal / vehicle the camera owner's category filter lets through (CP 159). */
  camera_categories: MyPushCategory[]
  /** The person's own "push to my devices" switch (NotificationSettings.push_enabled). */
  my_push_enabled: boolean
  /** Graffiti (critical) detection is on for this camera. */
  critical_enabled: boolean
}

/** GET (and PUT) /cameras/{id}/my-push/. */
export interface CameraMyPush {
  camera: number
  mode: MyPushMode
  /** The ticked subset for `categories`, else []. */
  categories: MyPushCategory[]
  /** #406 — the Suspicious switch as stored (absent on an old API = on). */
  suspicious?: boolean
  /** The person is one of the camera's users (owner, direct share, shared group). False: read-only. */
  in_audience: boolean
  limits: MyPushLimits
}

/** PUT /cameras/{id}/my-push/ and POST /camera-groups/{id}/my-push/: `categories` non-empty only for `categories`.
 *  #406 — `suspicious`: the switch under "All motion" and "Humans / Animals / Vehicles" (true by default); the
 *  server writes it true for `suspicious` and false for `critical_only` whatever is sent; never sent for `account`. */
export interface MyPushRequest {
  mode: MyPushChoice
  categories: MyPushCategory[]
  suspicious?: boolean
}

/** A per-camera filter map exactly as stored and read by the handler: the push categories plus the `_mode` marker
 *  (and, #406, the `suspicious` switch; absent = on). */
export type StoredPushFilters = Partial<PushFilters> & { _mode?: string }

export interface MyPushCameraRow {
  mode: MyPushMode
  categories: MyPushCategory[]
  filters: StoredPushFilters
}

/** GET /notifications/push-preferences/cameras/: the person's camera rows on cameras they are still a user of. */
export interface MyPushCameraRows {
  cameras: Record<string, MyPushCameraRow>
}

/** GET /camera-groups/{id}/my-push/: how many of the member cameras the person is a user of are on each mode. */
export interface GroupMyPushSummary {
  group?: { id: number; name: string; member_count: number }
  counts: Partial<Record<MyPushMode, number>>
  /** Member cameras the person is not a user of. */
  not_camera_user?: number
}

/** POST /camera-groups/{id}/my-push/ (dry run or real). Ids ascending. */
export interface GroupMyPushResult {
  group?: { id: number; name: string; member_count: number }
  mode: MyPushChoice
  categories: MyPushCategory[]
  suspicious?: boolean // #406
  dry_run?: boolean
  /** Changed — or, in a dry run, would change. */
  updated: number[]
  unchanged: number[]
  /** Member cameras the person is not a user of: untouched. */
  skipped: Array<{ id: number; reason: 'not_camera_user' }>
}

export interface PushCatalogGroup {
  category: PushCategory
  class_names: string[]
}

export interface PushCatalog {
  groups: PushCatalogGroup[]
}

/** #297 — the browser's answer about desktop notifications; 'unsupported' where the page cannot show them. */
export type DesktopAlertPermission = NotificationPermission | 'unsupported'

/** #297 — one desktop (system) notification for a live alert. */
export interface DesktopAlert {
  /** The event it announces: its tag (a repeat replaces, not stacks). */
  eventId?: number
  title: string
  body: string
  /** The event thumbnail; shown where the OS supports notification images. */
  image?: string
  /** D6: stays on screen until clicked — a critical event (item 294). */
  critical?: boolean
  /** What a click opens (the dashboard is brought to the front first). */
  onOpen?: () => void
}

// #311 L6 — G100 live view: api/cameras/<id>/g100/live/ (device_cloud/owner_live.py). The browser gets a
// signed signalling URL and TURN servers, never an AWS key.
export interface G100LiveSession {
  id: number
  state: 'starting' | 'live' | 'ended'
  created_at: string
  live_at: string | null
  ended_at: string | null
  ends_by: string                    // created_at + the maximum session length
  end_reason: string | null          // ours: USER_STOPPED, LIVE_START_TIMEOUT, NO_VIEWER, MAX_SESSION, ...
  camera_reason: string | null       // the camera's liveFinish reason (V1.1 section 9)
  viewers: number
  max_viewers: number
  params?: G100LiveStartValues | null   // #381: the values this session was started with
}

export interface G100LiveIceServer {
  urls: string[]
  username?: string
  credential?: string
}

export interface G100LiveConnection {
  channel_arn: string
  region: string
  signed_url: string                 // carries a 299 s viewer-only token: never log or show it
  ice_servers: G100LiveIceServer[]
  expires_in: number
}

export interface G100LiveViewerRef {
  id: number
  client_id: string
}

export interface G100LiveStatus {
  session: G100LiveSession | null
  enabled: boolean                   // #311 Task 10: false where a start would be refused for configuration
  defaults: G100LiveStartValues      // #381: what a plain start sends (the server's values)
}

/** #381 — the three 6.12.19 start values an engineer may vary from the console's Commands card.
 *  Region, channel, credentials and expiry stay the server's. */
export interface G100LiveStartValues {
  maximumSessionLength: number       // seconds
  idlePeriod: number                 // seconds
  codec: 'H264' | 'H265'
}
export type G100LiveStartRequest = Partial<G100LiveStartValues>

export interface G100LiveStart {
  session: G100LiveSession
  viewer: G100LiveViewerRef
  connection: G100LiveConnection
}

/** #414 — the picture size this browser receives (the <video> element's own size). */
export interface G100ReceivedSize { width: number; height: number }

/** #414 — the camera's liveRes setting (6.14.4): 1 = 720P, 2 = 1080P. */
export type G100LiveRes = 1 | 2

/** #414 — what the browser's WebRTC stats say about the received video; null fields = not reported. */
export interface G100LiveVideoStats {
  framesDecoded: number
  framesPerSecond: number | null
  frameWidth: number | null
  frameHeight: number | null
  timestamp: number                  // the report's own time, ms
}
